import type {
  RouteConfig,
  RouteType,
  ExtractRouteMethods,
  FilterNever,
} from "./types";
import { ComponentType } from "react";

// Type guard to check if routes are in Record format
function isRecordRoutes(
  routes: RouteConfig[] | Record<string, RouteType>
): routes is Record<string, RouteType> {
  return typeof routes === "object" && !Array.isArray(routes);
}

// Helper function to create a route definition
// Automatically generates build function from pattern
export function route(
  pattern: string,
  component: ComponentType<any>
): RouteType {
  // Extract parameter names from pattern (e.g., "/products/:id/edit" -> ["id"])
  const paramMatches = pattern.matchAll(/:(\w+)/g);
  const params: string[] = [];
  for (const match of paramMatches) {
    params.push(match[1]);
  }

  // Generate build function
  const build =
    params.length === 0
      ? () => pattern // Static route
      : (...args: any[]) => {
          let result = pattern;
          params.forEach((param, index) => {
            result = result.replace(`:${param}`, String(args[index]));
          });
          return result;
        };

  return {
    path: pattern,
    build,
    component,
  };
}

/**
 * Normalize routes to RouteConfig[] array format
 * Handles both RouteConfig[] and Record<string, RouteType> formats
 */
function toRouteConfigArray(
  routes: RouteConfig[] | Record<string, RouteType>
): RouteConfig[] {
  if (isRecordRoutes(routes)) {
    return Object.values(routes) as RouteConfig[];
  }
  return routes;
}

/**
 * Extract build functions from route definitions for navigation
 * Only works with Record<string, RouteType> format
 */
function extractRoutes<T extends Record<string, RouteType>>(
  routeDefs: T
): ExtractRouteMethods<T> {
  return Object.fromEntries(
    Object.entries(routeDefs).map(([name, def]) => [name, def.build])
  ) as ExtractRouteMethods<T>;
}

/**
 * Normalize a route group to RouteConfig[] format
 * Automatically detects and converts the format
 */
export function normalizeRouteGroup(
  routeGroup: RouteConfig[] | Record<string, RouteType>
): RouteConfig[] {
  return toRouteConfigArray(routeGroup);
}

/**
 * Generate RouteConfig[] array from a registry of route groups
 * Handles both RouteConfig[] and Record<string, RouteType> formats
 */
export function generateRouteConfigs(
  routeGroups: Record<string, RouteConfig[] | Record<string, RouteType>>
): RouteConfig[] {
  return Object.values(routeGroups)
    .map((group) => normalizeRouteGroup(group))
    .flat();
}

/**
 * Generate routes object for navigation from a registry
 * Only includes routes defined as Record<string, RouteType> format
 */
export function generateRoutesObject<
  T extends Record<string, RouteConfig[] | Record<string, RouteType>>
>(
  routeGroups: T
): FilterNever<{
  [K in keyof T]: T[K] extends Record<string, RouteType>
    ? ExtractRouteMethods<T[K]>
    : never;
}> {
  const routes: Record<string, any> = {};

  Object.entries(routeGroups).forEach(([name, group]) => {
    if (isRecordRoutes(group)) {
      routes[name] = extractRoutes(group);
    }
  });

  return routes as FilterNever<{
    [K in keyof T]: T[K] extends Record<string, RouteType>
      ? ExtractRouteMethods<T[K]>
      : never;
  }>;
}
